import { useGridEditor } from "./GridEditorContext";
import { useMemo, useRef, useEffect, useState } from "react";
import type { ParsedSvgElement } from "../../utils/svgUtils";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";

export function GridEditorViewer() {
    const {
        objectSvg,
        elements,
        selectedElementIds,
        viewBoxAttr,
        gridSvgContent,
        selectElement,
        clearSelection,
        selectAllElements,
        setSelectedElements,
        startDrag,
        updateDrag,
        endDrag,
        copyElements,
        pasteElements,
        undo,
        redo,
    } = useGridEditor();

    useUndoRedoKeyboard({ undo, redo });

    const objectSvgRef = useRef<SVGSVGElement>(null);
    const gridSvgRef = useRef<SVGSVGElement>(null);
    const isDragging = useRef(false);
    const dragStartPos = useRef<{ x: number; y: number } | null>(null);
    const [isSelecting, setIsSelecting] = useState(false);
    /** Bumps on mousemove during rect selection so refs update the selection overlay. */
    const [selectionUpdate, setSelectionUpdate] = useState(0);
    const selectionStart = useRef<{ x: number; y: number } | null>(null);
    const selectionEnd = useRef<{ x: number; y: number } | null>(null);
    /** Ignore the click that follows mouseup after rectangle selection (would otherwise clear). */
    const suppressNextBackgroundClickClear = useRef(false);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "c") {
                e.preventDefault();
                copyElements();
            } else if ((e.ctrlKey || e.metaKey) && e.key === "v") {
                e.preventDefault();
                pasteElements();
            } else if ((e.ctrlKey || e.metaKey) && e.key === "a") {
                e.preventDefault();
                selectAllElements();
            } else if (e.key === "Escape") {
                clearSelection();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [copyElements, pasteElements, clearSelection, selectAllElements]);

    // Handle mouse events for dragging and rectangle selection
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!objectSvgRef.current) return;

            const svgElement = objectSvgRef.current;
            const rect = svgElement.getBoundingClientRect();

            // Get viewBox dimensions
            const viewBoxParts = viewBoxAttr.split(/\s+/).map(Number);
            if (viewBoxParts.length !== 4) return;
            const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] =
                viewBoxParts;

            // Calculate mouse position relative to SVG element
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            // Convert to viewBox coordinates
            const scaleX = viewBoxWidth / rect.width;
            const scaleY = viewBoxHeight / rect.height;

            const svgX = viewBoxX + mouseX * scaleX;
            const svgY = viewBoxY + mouseY * scaleY;

            if (isDragging.current && dragStartPos.current) {
                updateDrag(svgX, svgY);
            } else if (isSelecting && selectionStart.current) {
                selectionEnd.current = { x: svgX, y: svgY };
                // Force re-render to show selection rectangle
                setSelectionUpdate((prev) => prev + 1);
            }
        };

        const handleMouseUp = () => {
            if (isDragging.current) {
                isDragging.current = false;
                dragStartPos.current = null;
                endDrag();
            } else if (isSelecting) {
                // Finalize rectangle selection
                if (
                    selectionStart.current &&
                    selectionEnd.current &&
                    objectSvgRef.current
                ) {
                    const start = selectionStart.current;
                    const end = selectionEnd.current;

                    // Calculate selection rectangle bounds
                    const minX = Math.min(start.x, end.x);
                    const maxX = Math.max(start.x, end.x);
                    const minY = Math.min(start.y, end.y);
                    const maxY = Math.max(start.y, end.y);

                    // Find elements that intersect or are inside the rectangle
                    const selectedIds = new Set<string>();
                    for (const element of elements) {
                        const bounds = getElementBounds(element);
                        if (bounds) {
                            // Check if element intersects or is inside selection rectangle
                            const elementRight = bounds.x + bounds.width;
                            const elementBottom = bounds.y + bounds.height;

                            const intersects =
                                bounds.x <= maxX &&
                                elementRight >= minX &&
                                bounds.y <= maxY &&
                                elementBottom >= minY;

                            if (intersects) {
                                selectedIds.add(element.id);
                            }
                        }
                    }

                    // Update selection - replace current selection with rectangle selection
                    if (selectedIds.size > 0) {
                        setSelectedElements(Array.from(selectedIds));
                    } else {
                        clearSelection();
                    }

                    setIsSelecting(false);
                    selectionStart.current = null;
                    selectionEnd.current = null;
                    suppressNextBackgroundClickClear.current = true;
                }
            }
        };

        // Always attach listeners - they check the state/refs internally
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [viewBoxAttr, updateDrag, endDrag, elements, setSelectedElements, clearSelection, isSelecting]);

    const handleElementMouseDown = (
        e: React.MouseEvent<SVGElement>,
        elementId: string
    ) => {
        e.stopPropagation();
        
        // Prevent rectangle selection when clicking on an element
        setIsSelecting(false);
        selectionStart.current = null;
        selectionEnd.current = null;
        
        // Handle selection first
        selectElement(elementId, e.shiftKey || e.ctrlKey || e.metaKey);
        
        // Only start dragging if the element is already selected or if we're not doing multi-select
        const isSelected = selectedElementIds.has(elementId);
        if (!isSelected && (e.shiftKey || e.ctrlKey || e.metaKey)) {
            // Just selecting, don't start drag
            return;
        }
        
        if (!objectSvgRef.current) return;

        const svgElement = objectSvgRef.current;
        const rect = svgElement.getBoundingClientRect();

        // Get viewBox dimensions
        const viewBoxParts = viewBoxAttr.split(/\s+/).map(Number);
        if (viewBoxParts.length !== 4) return;
        const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] = viewBoxParts;

        // Calculate mouse position relative to SVG element
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Convert to viewBox coordinates
        const scaleX = viewBoxWidth / rect.width;
        const scaleY = viewBoxHeight / rect.height;

        const svgX = viewBoxX + mouseX * scaleX;
        const svgY = viewBoxY + mouseY * scaleY;

        // Get element's current position
        const element = elements.find((el) => el.id === elementId);
        if (!element) return;

        // Parse transform to get current position
        let currentX = 0;
        let currentY = 0;
        if (element.transform) {
            const translateMatch = element.transform.match(
                /translate\(([^,]+),\s*([^)]+)\)/
            );
            if (translateMatch) {
                currentX = parseFloat(translateMatch[1]) || 0;
                currentY = parseFloat(translateMatch[2]) || 0;
            }
        }

        // Calculate offset
        const offsetX = svgX - currentX;
        const offsetY = svgY - currentY;

        isDragging.current = true;
        dragStartPos.current = { x: svgX, y: svgY };
        startDrag(elementId, offsetX, offsetY);
    };

    const handleSvgMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
        // Only start rectangle selection if clicking on the SVG background
        if (e.target === e.currentTarget && !isDragging.current) {
            e.preventDefault();
            if (!objectSvgRef.current) return;

            const svgElement = objectSvgRef.current;
            const rect = svgElement.getBoundingClientRect();

            // Get viewBox dimensions
            const viewBoxParts = viewBoxAttr.split(/\s+/).map(Number);
            if (viewBoxParts.length !== 4) return;
            const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] =
                viewBoxParts;

            // Calculate mouse position relative to SVG element
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            // Convert to viewBox coordinates
            const scaleX = viewBoxWidth / rect.width;
            const scaleY = viewBoxHeight / rect.height;

            const svgX = viewBoxX + mouseX * scaleX;
            const svgY = viewBoxY + mouseY * scaleY;

            setIsSelecting(true);
            selectionStart.current = { x: svgX, y: svgY };
            selectionEnd.current = { x: svgX, y: svgY };
        }
    };

    const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
        if (e.target !== e.currentTarget) return;
        if (suppressNextBackgroundClickClear.current) {
            suppressNextBackgroundClickClear.current = false;
            return;
        }
        clearSelection();
    };

    // Calculate bounding box for an element
    const getElementBounds = (element: ParsedSvgElement): {
        x: number;
        y: number;
        width: number;
        height: number;
    } | null => {
        const attrs = element.attributes;
        let x = 0;
        let y = 0;
        let width = 0;
        let height = 0;

        switch (element.type) {
            case "rect": {
                x = parseFloat(attrs.x || "0");
                y = parseFloat(attrs.y || "0");
                width = parseFloat(attrs.width || "0");
                height = parseFloat(attrs.height || "0");
                break;
            }
            case "circle": {
                const cx = parseFloat(attrs.cx || "0");
                const cy = parseFloat(attrs.cy || "0");
                const r = parseFloat(attrs.r || "0");
                x = cx - r;
                y = cy - r;
                width = r * 2;
                height = r * 2;
                break;
            }
            case "ellipse": {
                const cx = parseFloat(attrs.cx || "0");
                const cy = parseFloat(attrs.cy || "0");
                const rx = parseFloat(attrs.rx || "0");
                const ry = parseFloat(attrs.ry || "0");
                x = cx - rx;
                y = cy - ry;
                width = rx * 2;
                height = ry * 2;
                break;
            }
            case "line": {
                const x1 = parseFloat(attrs.x1 || "0");
                const y1 = parseFloat(attrs.y1 || "0");
                const x2 = parseFloat(attrs.x2 || "0");
                const y2 = parseFloat(attrs.y2 || "0");
                x = Math.min(x1, x2);
                y = Math.min(y1, y2);
                width = Math.abs(x2 - x1);
                height = Math.abs(y2 - y1);
                // Ensure minimum size for handles
                if (width === 0) width = 20;
                if (height === 0) height = 20;
                break;
            }
            case "path": {
                // For paths, try to calculate bounding box using DOM API
                try {
                    const svgNs = "http://www.w3.org/2000/svg";
                    const tempSvg = document.createElementNS(svgNs, "svg");
                    tempSvg.setAttribute("viewBox", "-10000 -10000 20000 20000");
                    tempSvg.style.position = "absolute";
                    tempSvg.style.width = "0";
                    tempSvg.style.height = "0";
                    tempSvg.style.visibility = "hidden";
                    tempSvg.style.pointerEvents = "none";

                    const path = document.createElementNS(svgNs, "path");
                    path.setAttribute("d", attrs.d || "");
                    tempSvg.appendChild(path);
                    document.body.appendChild(tempSvg);

                    const bbox = path.getBBox();
                    document.body.removeChild(tempSvg);

                    x = bbox.x;
                    y = bbox.y;
                    width = bbox.width || 50;
                    height = bbox.height || 50;
                } catch {
                    x = 0;
                    y = 0;
                    width = 50;
                    height = 50;
                }
                break;
            }
            case "polygon":
            case "polyline": {
                // Calculate bounding box from points
                const points = attrs.points || "";
                const coords = points
                    .trim()
                    .split(/[\s,]+/)
                    .map(parseFloat)
                    .filter((n) => !isNaN(n));

                if (coords.length >= 4) {
                    const xs = coords.filter((_, i) => i % 2 === 0);
                    const ys = coords.filter((_, i) => i % 2 === 1);
                    x = Math.min(...xs);
                    y = Math.min(...ys);
                    width = Math.max(...xs) - x;
                    height = Math.max(...ys) - y;
                } else {
                    x = 0;
                    y = 0;
                    width = 50;
                    height = 50;
                }
                break;
            }
            default:
                return null;
        }

        // Parse transform to get current position offset
        let offsetX = 0;
        let offsetY = 0;
        if (element.transform) {
            const translateMatch = element.transform.match(
                /translate\(([^,]+),\s*([^)]+)\)/
            );
            if (translateMatch) {
                offsetX = parseFloat(translateMatch[1]) || 0;
                offsetY = parseFloat(translateMatch[2]) || 0;
            }
        }

        return {
            x: x + offsetX,
            y: y + offsetY,
            width,
            height,
        };
    };

    const handleHandleMouseDown = (
        e: React.MouseEvent<SVGCircleElement>,
        elementId: string
    ) => {
        e.stopPropagation();
        // Start dragging the element from the handle
        handleElementMouseDown(e as any, elementId);
    };

    // Rebuild object SVG with element transforms
    const objectSvgContent = useMemo(() => {
        if (!objectSvg) return null;

        // Sort elements by startIndex descending to replace from end to start
        const sortedElements = [...elements].sort(
            (a, b) => b.startIndex - a.startIndex
        );

        let result = objectSvg.content;
        for (const element of sortedElements) {
            // Build new element string with transform
            const attrs = { ...element.attributes };
            if (element.transform) {
                attrs.transform = element.transform;
            }

            const attrsString = Object.entries(attrs)
                .filter(([_, value]) => value !== undefined && value !== null)
                .map(([key, value]) => `${key}="${value}"`)
                .join(" ");

            const newElement = `<${element.type} ${attrsString} />`;

            result =
                result.slice(0, element.startIndex) +
                newElement +
                result.slice(element.endIndex);
        }

        return result;
    }, [objectSvg, elements]);

    const containerDimensions = useMemo(() => {
        const viewBoxParts = viewBoxAttr.split(/\s+/).map(Number);
        if (viewBoxParts.length !== 4) {
            return null;
        }
        const [, , viewBoxWidth, viewBoxHeight] = viewBoxParts;

        const baseHeight = 400;
        const aspectRatio = viewBoxWidth / viewBoxHeight;
        let baseWidth = baseHeight * aspectRatio;

        const maxBaseWidth = 800;
        if (baseWidth > maxBaseWidth) {
            baseWidth = maxBaseWidth;
            const adjustedHeight = baseWidth / aspectRatio;
            return {
                width: baseWidth,
                height: adjustedHeight,
            };
        }

        return {
            width: baseWidth,
            height: baseHeight,
        };
    }, [viewBoxAttr]);

    const gridViewBox = useMemo(() => {
        if (!gridSvgContent) return "0 0 200 200";
        const viewBoxMatch = gridSvgContent.match(/viewBox=["']([^"']+)["']/i);
        return viewBoxMatch ? viewBoxMatch[1] : "0 0 200 200";
    }, [gridSvgContent]);

    const gridContainerDimensions = useMemo(() => {
        const viewBoxParts = gridViewBox.split(/\s+/).map(Number);
        if (viewBoxParts.length !== 4) {
            return null;
        }
        const [, , viewBoxWidth, viewBoxHeight] = viewBoxParts;

        const baseHeight = 400;
        const aspectRatio = viewBoxWidth / viewBoxHeight;
        let baseWidth = baseHeight * aspectRatio;

        const maxBaseWidth = 800;
        if (baseWidth > maxBaseWidth) {
            baseWidth = maxBaseWidth;
            const adjustedHeight = baseWidth / aspectRatio;
            return {
                width: baseWidth,
                height: adjustedHeight,
            };
        }

        return {
            width: baseWidth,
            height: baseHeight,
        };
    }, [gridViewBox]);

    return (
        <div className="panel-viewer grid-editor-viewer">
            <div className="grid-editor-panes">
                <div className="grid-editor-pane">
                    <h3>Object SVG (Editable)</h3>
                    <div className="svg-scroll-wrapper">
                        <div
                            className="svg-container"
                            style={
                                containerDimensions
                                    ? {
                                          width: `${containerDimensions.width}px`,
                                          height: `${containerDimensions.height}px`,
                                      }
                                    : undefined
                            }
                        >
                            {objectSvg && elements.length > 0 ? (
                                <svg
                                    ref={objectSvgRef}
                                    viewBox={viewBoxAttr}
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="path-tool-svg"
                                    onClick={handleSvgClick}
                                    onMouseDown={handleSvgMouseDown}
                                >
                                    {elements.length > 200 ? (
                                        // For large SVGs, render as-is for better performance
                                        <g dangerouslySetInnerHTML={{
                                            __html: elements.map(el => {
                                                const attrs = { ...el.attributes };
                                                if (el.transform) {
                                                    attrs.transform = el.transform;
                                                }
                                                const attrsString = Object.entries(attrs)
                                                    .filter(([_, value]) => value !== undefined && value !== null)
                                                    .map(([key, value]) => `${key}="${value}"`)
                                                    .join(" ");
                                                return `<${el.type} ${attrsString} />`;
                                            }).join("")
                                        }} />
                                    ) : (
                                        <>
                                    {/* Selection rectangle */}
                                    {isSelecting &&
                                        selectionStart.current &&
                                        selectionEnd.current && (
                                            <rect
                                                key={selectionUpdate}
                                                x={Math.min(
                                                    selectionStart.current.x,
                                                    selectionEnd.current.x
                                                )}
                                                y={Math.min(
                                                    selectionStart.current.y,
                                                    selectionEnd.current.y
                                                )}
                                                width={Math.abs(
                                                    selectionEnd.current.x -
                                                        selectionStart.current.x
                                                )}
                                                height={Math.abs(
                                                    selectionEnd.current.y -
                                                        selectionStart.current.y
                                                )}
                                                fill="rgba(74, 144, 226, 0.1)"
                                                stroke="#4a90e2"
                                                strokeWidth="1.5"
                                                strokeDasharray="4,4"
                                                style={{
                                                    pointerEvents: "none",
                                                }}
                                            />
                                        )}
                                    {elements.map((element) => {
                                        const isSelected =
                                            selectedElementIds.has(element.id);
                                        
                                        // Build element string with transform
                                        const attrs = { ...element.attributes };
                                        if (element.transform) {
                                            attrs.transform = element.transform;
                                        }
                                        
                                        const attrsString = Object.entries(attrs)
                                            .filter(([_, value]) => value !== undefined && value !== null)
                                            .map(([key, value]) => `${key}="${value}"`)
                                            .join(" ");
                                        
                                        const elementString = `<${element.type} ${attrsString} />`;

                                        const bounds = isSelected
                                            ? getElementBounds(element)
                                            : null;

                                        return (
                                            <g key={element.id}>
                                                {isSelected && bounds && (
                                                    <>
                                                        {/* Bounding box outline */}
                                                        <rect
                                                            x={bounds.x}
                                                            y={bounds.y}
                                                            width={bounds.width}
                                                            height={bounds.height}
                                                            fill="none"
                                                            stroke="#4a90e2"
                                                            strokeWidth="1.5"
                                                            strokeDasharray="4,4"
                                                            style={{
                                                                pointerEvents: "none",
                                                            }}
                                                        />
                                                        {/* Corner handles */}
                                                        <circle
                                                            cx={bounds.x}
                                                            cy={bounds.y}
                                                            r="4"
                                                            fill="#4a90e2"
                                                            stroke="#ffffff"
                                                            strokeWidth="1.5"
                                                            style={{
                                                                cursor: "nwse-resize",
                                                            }}
                                                            onMouseDown={(e) =>
                                                                handleHandleMouseDown(
                                                                    e,
                                                                    element.id
                                                                )
                                                            }
                                                        />
                                                        <circle
                                                            cx={bounds.x + bounds.width}
                                                            cy={bounds.y}
                                                            r="4"
                                                            fill="#4a90e2"
                                                            stroke="#ffffff"
                                                            strokeWidth="1.5"
                                                            style={{
                                                                cursor: "nesw-resize",
                                                            }}
                                                            onMouseDown={(e) =>
                                                                handleHandleMouseDown(
                                                                    e,
                                                                    element.id
                                                                )
                                                            }
                                                        />
                                                        <circle
                                                            cx={bounds.x}
                                                            cy={bounds.y + bounds.height}
                                                            r="4"
                                                            fill="#4a90e2"
                                                            stroke="#ffffff"
                                                            strokeWidth="1.5"
                                                            style={{
                                                                cursor: "nesw-resize",
                                                            }}
                                                            onMouseDown={(e) =>
                                                                handleHandleMouseDown(
                                                                    e,
                                                                    element.id
                                                                )
                                                            }
                                                        />
                                                        <circle
                                                            cx={bounds.x + bounds.width}
                                                            cy={bounds.y + bounds.height}
                                                            r="4"
                                                            fill="#4a90e2"
                                                            stroke="#ffffff"
                                                            strokeWidth="1.5"
                                                            style={{
                                                                cursor: "nwse-resize",
                                                            }}
                                                            onMouseDown={(e) =>
                                                                handleHandleMouseDown(
                                                                    e,
                                                                    element.id
                                                                )
                                                            }
                                                        />
                                                        {/* Center handle */}
                                                        <circle
                                                            cx={bounds.x + bounds.width / 2}
                                                            cy={bounds.y + bounds.height / 2}
                                                            r="4"
                                                            fill="#4a90e2"
                                                            stroke="#ffffff"
                                                            strokeWidth="1.5"
                                                            style={{
                                                                cursor: "move",
                                                            }}
                                                            onMouseDown={(e) =>
                                                                handleHandleMouseDown(
                                                                    e,
                                                                    element.id
                                                                )
                                                            }
                                                        />
                                                    </>
                                                )}
                                                <g
                                                    onMouseDown={(e) =>
                                                        handleElementMouseDown(
                                                            e,
                                                            element.id
                                                        )
                                                    }
                                                    style={{
                                                        cursor: isSelected ? "move" : "pointer",
                                                        opacity: isSelected ? 1 : 0.9,
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: elementString,
                                                    }}
                                                />
                                            </g>
                                        );
                                    })}
                                        </>
                                    )}
                                </svg>
                            ) : objectSvg ? (
                                <svg
                                    ref={objectSvgRef}
                                    viewBox={viewBoxAttr}
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="path-tool-svg"
                                    onClick={handleSvgClick}
                                >
                                    <g
                                        dangerouslySetInnerHTML={{
                                            __html: objectSvg.content
                                                .replace(/<\/?svg[^>]*>/gi, "")
                                                .trim(),
                                        }}
                                    />
                                </svg>
                            ) : (
                                <div className="svg-placeholder">
                                    <p>Import an SVG file to begin editing</p>
                                    <p className="svg-placeholder-hint">
                                        Drag elements to reposition them
                                    </p>
                                    <p className="svg-placeholder-hint">
                                        Use Ctrl+C and Ctrl+V to copy and paste
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="grid-editor-pane">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <h3 style={{ margin: 0 }}>Grid Pattern (Read-only)</h3>
                        {gridSvgContent && (
                            <button
                                type="button"
                                onClick={() => {
                                    const blob = new Blob([gridSvgContent], { type: "image/svg+xml;charset=utf-8" });
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement("a");
                                    link.href = url;
                                    link.download = `grid-pattern-${Date.now()}.svg`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(url);
                                }}
                                style={{
                                    padding: "6px 12px",
                                    fontSize: "14px",
                                    backgroundColor: "#4a90e2",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = "#357abd";
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = "#4a90e2";
                                }}
                            >
                                Download SVG
                            </button>
                        )}
                    </div>
                    <div className="svg-scroll-wrapper">
                        <div
                            className="svg-container"
                            style={
                                gridContainerDimensions
                                    ? {
                                          width: `${gridContainerDimensions.width}px`,
                                          height: `${gridContainerDimensions.height}px`,
                                      }
                                    : undefined
                            }
                        >
                            {gridSvgContent ? (
                                <svg
                                    ref={gridSvgRef}
                                    viewBox={gridViewBox}
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="path-tool-svg"
                                >
                                    <g
                                        dangerouslySetInnerHTML={{
                                            __html: gridSvgContent
                                                .replace(/<\/?svg[^>]*>/gi, "")
                                                .trim(),
                                        }}
                                    />
                                </svg>
                            ) : (
                                <div className="svg-placeholder">
                                    <p>Grid will appear here</p>
                                    <p className="svg-placeholder-hint">
                                        Edit the object SVG to see the grid
                                        pattern
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

